import type { CargoItem, CargoListFilters } from '@/types/cargoList.types';
import { CARGO_SEGMENT_FILTER_ALL, getCargoSegmentLabel } from '@/constants/cargoList.constants';

/**
 * Filter cargo items based on search query and filters
 */
export function filterCargoItems(items: CargoItem[], filters: CargoListFilters): CargoItem[] {
  return items.filter((item) => {
    // Search query filter (searches in charterer, trade, and status)
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const searchableText =
        `${item.charterer} ${item.trade} ${item.status} ${item.certainty}`.toLowerCase();
      if (!searchableText.includes(query)) {
        return false;
      }
    }

    // Segment filter (VL / SM). Compared case-insensitively against the raw view
    // value; a row with no segment is excluded from a specific-segment view
    // rather than leaking into both.
    if (filters.segmentFilter !== CARGO_SEGMENT_FILTER_ALL) {
      if (item.segment?.trim().toUpperCase() !== filters.segmentFilter) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date and time separately for display
 */
export function formatDateTime(dateString: string): { date: string; time: string } {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
  };
}

/**
 * Format date range for display
 */
export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start.toDateString() === end.toDateString()) {
    return formatDate(startDate);
  }

  const startFormatted = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const endFormatted = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Format a commission decimal (e.g. 0.0125) as a percentage with 2-4 decimals,
 * matching the Angular pipe `number: '1.2-4'`. Returns '--' for zero/null/undefined.
 */
export function formatCommission(decimal: number | null | undefined): string {
  if (!decimal) return '--';
  return (
    (decimal * 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }) + '%'
  );
}

/**
 * Format cargo size with proper units
 */
export function formatCargoSize(size: number): string {
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M MT`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}k MT`;
  }
  return `${size.toLocaleString()} MT`;
}

/**
 * Get badge variant for privacy level
 */
export function getPrivacyLevelBadgeVariant(
  privacyLevel: string
): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (privacyLevel.toLowerCase()) {
    case 'public':
      return 'success';
    case 'stealth':
      return 'warning';
    case 'private':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get badge variant for status
 */
export function getStatusBadgeVariant(
  status: string
): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (status.toLowerCase()) {
    case 'confirmed':
      return 'success';
    case 'reported':
      return 'primary';
    case 'cancelled':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Resolves the display label + Badge variant for a cargo's `segment`.
 * Returns null for a missing or unrecognised value so callers render nothing.
 *
 * Deliberately mirrors `fixtureUtils.getFixtureVesselSizeInfo` - VLCC blue,
 * Suezmax green - so the same vessel class reads the same across the app. It
 * stays a separate function because cargo keys off the view's 'VL'/'SM' strings
 * while Fixtures keys off a numeric id whose encoding differs from Position
 * List's.
 */
export function getCargoSegmentInfo(
  segment: string | null | undefined
): { label: string; variant: 'primary' | 'success' } | null {
  const label = getCargoSegmentLabel(segment);
  if (!label) return null;
  return { label, variant: label === 'VLCC' ? 'primary' : 'success' };
}
